import React, { useState } from 'react';
import {
  FileText,
  Download,
  Filter,
  TrendingUp,
  Users,
  Zap,
  DollarSign,
  AlertCircle,
  Loader2,
  Calendar,
  Receipt
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { PERMISSIONS } from '../utils/permissions';
const API_URL = process.env.REACT_APP_API_URL || 'https://api.hte.sn';

const reportTypes = [
  {
    value: 'transactions',
    label: 'Rapport des Transactions',
    icon: DollarSign,
    description: 'Historique complet avec statistiques de revenus et kWh totaux',
    endpoint: '/api/reports/transactions'
  },
  {
    value: 'compteurs',
    label: 'Rapport des Compteurs',
    icon: Zap,
    description: "État et statistiques des compteurs",
    endpoint: '/api/reports/compteurs'
  },
  {
    value: 'clients',
    label: 'Rapport des Clients',
    icon: Users,
    description: "Analyse comportementale et informations clients",
    endpoint: '/api/reports/clients'
  },
  {
    value: 'revenus',
    label: 'Rapport des Revenus',
    icon: TrendingUp,
    description: "Analyse financière détaillée avec statistiques",
    endpoint: '/api/reports/revenus'
  },
  {
    value: 'redevances',
    label: 'Rapport des Redevances',
    icon: Receipt,
    description: "Suivi des redevances mensuelles avec taux de collecte et clients payeurs/impayés",
    endpoint: '/api/reports/redevances'
  },
  {
    value: 'monitoring-clients',
    label: 'Surveillances Clients à Risque',
    icon: AlertCircle,
    description: "Analyse des clients par niveau de risque (actif, à surveiller, risque élevé) avec détails financiers",
    endpoint: '/api/reports/monitoring-clients'
  },
  {
    value: 'alertes',
    label: 'Rapport des Alertes',
    icon: AlertCircle,
    description: "Événements et incidents système",
    endpoint: '/api/reports/alertes'
  }
];

const ReportCard = ({ type, label, icon: Icon, description, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`relative group text-left p-6 rounded-xl border-2 transition-all w-full ${
      selected ? 'border-blue-600 bg-blue-50 shadow-lg' : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
    }`}
  >
    <div className="flex items-start gap-4">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${selected ? 'bg-blue-600' : 'bg-blue-100 group-hover:bg-blue-200'}`}>
        <Icon className={`w-6 h-6 ${selected ? 'text-white' : 'text-blue-600'}`} />
      </div>
      <div className="flex-1">
        <h3 className={`font-semibold mb-1 ${selected ? 'text-blue-900' : 'text-gray-900'}`}>{label}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
    {selected && (
      <div className="absolute top-3 right-3">
        <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    )}
  </button>
);

const Rapports = () => {
  const { hasPermission } = usePermissions();
  const [type, setType] = useState('transactions');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [formatExport, setFormatExport] = useState('xlsx');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [redevanceStatus, setRedevanceStatus] = useState('');
  const [riskLevel, setRiskLevel] = useState('');

  const selectedReport = reportTypes.find(r => r.value === type);

  // Fonction pour définir des plages de dates rapides
  const setQuickDateRange = (range) => {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];
    
    switch(range) {
      case 'today':
        setStartDate(formatDate(today));
        setEndDate(formatDate(today));
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        setStartDate(formatDate(weekAgo));
        setEndDate(formatDate(today));
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(today.getMonth() - 1);
        setStartDate(formatDate(monthAgo));
        setEndDate(formatDate(today));
        break;
      case 'year':
        const yearAgo = new Date(today);
        yearAgo.setFullYear(today.getFullYear() - 1);
        setStartDate(formatDate(yearAgo));
        setEndDate(formatDate(today));
        break;
      default:
        break;
    }
  };

 const downloadReport = async () => {
  if (!hasPermission(PERMISSIONS.EXPORT_REPORTS)) {
    setMessage('❌ Vous n\'avez pas la permission d\'exporter des rapports');
    return;
  }
  setLoading(true);
  setMessage('');

  try {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('format', formatExport);
    
    // ✅ Paramètres spécifiques
    if (type === 'redevances' && redevanceStatus) {
      params.append('status', redevanceStatus);
    }
    if (type === 'monitoring-clients' && riskLevel) {
      params.append('risk_level', riskLevel);
    }

    const url = `${API_URL}${selectedReport.endpoint}?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur ${response.status}: ${errorText}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    
    const extensions = {
      'xlsx': 'xlsx',
      'pdf': 'pdf',
      'csv': 'csv',
    };
    const extension = extensions[formatExport] || 'csv';
    const filename = `rapport_${type}_${new Date().toISOString().split('T')[0]}.${extension}`;
    link.download = filename;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    setMessage(`✅ Rapport téléchargé avec succès: ${filename}`);
  } catch (error) {
    console.error('Erreur téléchargement:', error);
    setMessage(`❌ Erreur: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8" />
          <div>
            <h1 className="text-2xl font-bold">Rapports et Analyses</h1>
            <p className="text-green-100 text-sm mt-1">Générez des rapports détaillés et exportez vos données en CSV, Excel ou PDF</p>
          </div>
        </div>
      </div>

      {/* Message de statut */}
      {message && (
        <div className={`p-4 rounded-lg ${message.includes('✅') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {message}
        </div>
      )}

      {/* Configuration du rapport */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-bold text-gray-900">Type de Rapport</h2>
        </div>

        {/* Types de rapports */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {reportTypes.map((r) => (
            <ReportCard
              key={r.value}
              {...r}
              selected={type === r.value}
              onClick={() => setType(r.value)}
            />
          ))}
        </div>

        {/* Plages de dates rapides */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Plages de dates rapides
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setQuickDateRange('today')}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Aujourd'hui
            </button>
            <button
              onClick={() => setQuickDateRange('week')}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              7 derniers jours
            </button>
            <button
              onClick={() => setQuickDateRange('month')}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              30 derniers jours
            </button>
            <button
              onClick={() => setQuickDateRange('year')}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              12 derniers mois
            </button>
          </div>
        </div>
        {/* Filtres */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date début
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date fin
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Format d'export
            </label>
            <select
              value={formatExport}
              onChange={(e) => setFormatExport(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
               <option value="xlsx">Excel (.xlsx)</option>
              <option value="pdf">PDF (Document imprimable)</option>
              <option value="csv">CSV (Excel compatible)</option>
             
            </select>
          </div>
        </div>
         {/* Filtres supplémentaires selon le type */}
          {type === 'redevances' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filtrer par statut
              </label>
              <select
                value={redevanceStatus}
                onChange={(e) => setRedevanceStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tous les statuts</option>
                <option value="paid">Payées</option>
                <option value="unpaid">Impayées</option>
                <option value="partial">Partiellement payées</option>
              </select>
            </div>
          )}

          {type === 'monitoring-clients' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Niveau de risque
              </label>
              <select
                value={riskLevel}
                onChange={(e) => setRiskLevel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tous les niveaux</option>
                <option value="actif">✅ Actifs (bon score)</option>
                <option value="a_surveiller">⚠️ À surveiller</option>
                <option value="risque_eleve">❌ Risque élevé</option>
              </select>
            </div>
          )}

        {/* Boutons d'action */}
        <div className="flex gap-3">
           <PermissionGuard permission={PERMISSIONS.EXPORT_REPORTS}>
          <button
            onClick={downloadReport}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Génération en cours...</span>
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                <span>Générer et Télécharger</span>
              </>
            )}
          </button>
          </PermissionGuard>
          {!hasPermission(PERMISSIONS.EXPORT_REPORTS) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-red-800 font-medium">
                🔒 Vous n'avez pas la permission d'exporter des rapports
              </p>
              <p className="text-red-600 text-sm mt-2">
                Contactez votre administrateur pour obtenir l'accès
              </p>
            </div>
          )}
      
          <button
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setRedevanceStatus('');
              setRiskLevel('');
              setMessage('');
            }}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Informations sur le rapport sélectionné */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
        <h3 className="font-semibold text-blue-900 mb-2">
          Rapport sélectionné: {selectedReport?.label}
        </h3>
        <p className="text-blue-700 text-sm mb-4">
          {selectedReport?.description}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-900">Format:</span>{' '}
            <span className="text-blue-700">
               {formatExport === 'xlsx' && 'Excel (.xlsx)'}
               {formatExport === 'pdf' && 'PDF (Document imprimable)'}
              {formatExport === 'csv' && 'CSV (Excel compatible)'}
                          
            </span>
          </div>
          <div>
            <span className="font-medium text-blue-900">Période:</span>{' '}
            <span className="text-blue-700">
              {startDate && endDate
                ? `${startDate} → ${endDate}`
                : startDate
                ? `À partir du ${startDate}`
                : endDate
                ? `Jusqu'au ${endDate}`
                : 'Toutes les données'}
            </span>
          </div>
          <div>
            <span className="font-medium text-blue-900">Type:</span>{' '}
            <span className="text-blue-700">{selectedReport?.label}</span>
          </div>
        </div>
      </div>

      {/* Guide d'utilisation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">
          Guide d'utilisation
        </h2>
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
              1
            </div>
            <p>Sélectionnez le type de rapport que vous souhaitez générer</p>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
              2
            </div>
            <p>Utilisez les boutons de plages rapides ou définissez une période personnalisée (optionnel)</p>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
              3
            </div>
            <p>Choisissez le format d'export: CSV pour Excel, XLSX pour un fichier Excel natif, ou PDF pour un document imprimable</p>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
              4
            </div>
            <p>Cliquez sur "Générer et Télécharger" pour obtenir votre rapport</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Rapports;